import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { useTranslation } from "react-i18next";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { getInitialResultsViewMode } from "~/features/search/utils/searchUtils";
import classes from "./ProductResults.module.less";
import { Alert } from "~/common/mui/Alert/Alert";
import { Box, Stack } from "~/common/mui";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import Spinner from "~/common/components/Spinner/Spinner";
import { TopFilterBar } from "./components/TopFilterBar";
import { SkeletonResultList } from "./views/ListView/components/SkeletonResultList";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useSearchPagination } from "./useSearchPagination";
import { selectIsUseNewDesign } from "~/domains/userPreferences/redux/selectors";
import { L1CategoryNavigation } from "../L1CategoryNavigation";
import { NewDesignReduxProductTabularView } from "./views/NewTabularView/NewTabularView";
import { NewTabularViewSkeleton } from "./views/NewTabularView/NewTabularViewSkeleton";
import { memo, type ReactNode, useCallback, useLayoutEffect } from "react";
import { SearchDisplayMode } from "~/features/search/models/models";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { clearAll, clearAllTechFilters, clearAllFacetFilters } from "~/features/search/redux/actions";
import { Pagination } from "~/features/search/components/common/Pagination";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { useDispatch, useSelector } from "react-redux";
import { SeriesTreeView } from "~/features/search/components/ProductResults/views/SeriesTreeView/SeriesTreeView";
import { ReduxProductTabularView } from "~/features/search/components/ProductResults/views/TabularView/ReduxProductTabularView";
import { selectIsInFlight, selectIsInit, selectProductResults } from "~/features/search/redux/searchSelectors";
import NotFoundContactUsBar from "~/features/search/components/common/NotFoundContactUsBar/NotFoundContactUsBar";
import SearchResultList from "~/features/search/components/ProductResults/views/ListView/SearchResultList";
import { useResetQualtrics } from "~/features/Analytics/hooks/useResetQualtrics";
import { useReduxUnitMode } from "~/domains/userPreferences/hooks/useReduxUnitMode";
import { NoSearchResults } from "~/features/search/components/common/NoSearchResults";
import type { RootState } from "~/app/store";

import { useSRPResultsViewMode } from "./useSRPResultsViewMode";
import { getTableFullbleedStyle } from "./fullbleedUtils";

interface Props {
  recommendationPosition?: number;
  recommendationComponent?: ReactNode;
}

const ProductResults = ({ recommendationPosition, recommendationComponent }: Props) => {
  const { t } = useTranslation();
  const screenSize = useNumericScreenSize();
  const dispatch = useDispatch();
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const results = useSelector(selectProductResults);
  const appliedFilters = useSelectAppliedFilters();

  const pageType = useSearchPageType();
  // TODO - other cases! Campaign page?
  const gaInteractionType = pageType === SearchPageType.SEARCH ? "Search" : "PLP";

  const inFlight = useSelector(selectIsInFlight);
  const isInit = useSelector(selectIsInit);

  const useNewDesign = useSelector(selectIsUseNewDesign);

  const [displayMode, setDisplayMode, isTechnicalViewVisible] = useSRPResultsViewMode();

  const productCodes = useSelector((state: RootState) => state.srp.results.results.map((row) => row.productCode));

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const hasSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
  );

  const tariffSurcharge = productDescriptorList.some(
    (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
  );

  useLayoutEffect(() => {
    if (isInit && !isProductFamilySelected) {
      setDisplayMode(getInitialResultsViewMode());
    }
  }, [isInit, isProductFamilySelected]);

  const { pageAmount, currentPage, onPageChanged } = useSearchPagination();

  useResetQualtrics([inFlight]);

  const { value: unitMode, onChange: setUnitMode } = useReduxUnitMode(gaInteractionType, () => {
    dispatch(clearAllTechFilters());
    dispatch(clearAllFacetFilters(unitMode));
  });

  const handleClearAll = useCallback(() => {
    dispatch(clearAll(pageType));
  }, [dispatch, pageType]);

  const tabularViewConfigured = isProductFamilySelected || pageType === SearchPageType.PLP;
  const showChildCategoryNavigation = !isProductFamilySelected && pageType == SearchPageType.SEARCH;

  const showSkeletons =
    displayMode === SearchDisplayMode.LIST || (useNewDesign && displayMode === SearchDisplayMode.TABULAR);

  let paginationComponent = null;
  if (pageAmount > 1) {
    paginationComponent = (
      <Pagination
        classes={{ root: "scha__srp__pagination" }}
        pageAmount={pageAmount}
        currentPage={currentPage}
        onPageChange={onPageChanged}
        renderHrefs={true}
        goToIdAfterChange={"#resultsHeader"}
      />
    );
  }
  if (displayMode === SearchDisplayMode.TABULAR && useNewDesign) {
    // Pagination handled by the new tabular view
    paginationComponent = null;
  }

  if (!isInit || results.length) {
    return (
      <Stack
        id={"productResults"}
        sx={{
          gap: { phone: 2, "desktop-s": 3 },
        }}
        className={classes.containerVariable}
      >
        {isInit && (
          <TopFilterBar
            unitMode={unitMode}
            displayMode={displayMode}
            setUnitMode={setUnitMode}
            setDisplayMode={setDisplayMode}
            newDesign={useNewDesign}
            canShowNewDesignTabular={isTechnicalViewVisible}
          />
        )}

        {showChildCategoryNavigation && <L1CategoryNavigation />}

        {inFlight && !showSkeletons && <Spinner className="spinner--result-list" />}

        {(!inFlight || showSkeletons) && (
          <>
            {displayMode === SearchDisplayMode.TABULAR && (
              <>
                {useNewDesign && inFlight && (
                  <Box sx={getTableFullbleedStyle(screenSize)}>
                    <NewTabularViewSkeleton />
                  </Box>
                )}
                {useNewDesign && !inFlight && (
                  <Stack gap={4} sx={getTableFullbleedStyle(screenSize)}>
                    {tabularViewConfigured && <NewDesignReduxProductTabularView />}
                    {!tabularViewConfigured && (
                      <Alert severity={"info"} className={"scha__srp__select-category-msg"}>
                        {t("page.srp.selectCategory")}
                      </Alert>
                    )}
                  </Stack>
                )}
                {!useNewDesign && (
                  <Stack gap={4}>
                    {recommendationComponent}
                    {tabularViewConfigured && (
                      <>
                        <ReduxProductTabularView />
                        {paginationComponent}
                      </>
                    )}
                    {!tabularViewConfigured && (
                      <Alert severity={"info"} className={"scha__srp__select-category-msg"}>
                        {t("page.srp.selectCategory")}
                      </Alert>
                    )}
                  </Stack>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.LIST && (
              <>
                {inFlight ? (
                  <SkeletonResultList
                    amount={20}
                    recommendationPosition={recommendationPosition}
                    recommendationComponent={recommendationComponent}
                  />
                ) : (
                  <>
                    <SearchResultList
                      results={results}
                      recommendationPosition={recommendationPosition}
                      recommendationComponent={recommendationComponent}
                    />
                    {paginationComponent}
                  </>
                )}
              </>
            )}
            {displayMode === SearchDisplayMode.SERIES_TREE && <SeriesTreeView />}

            {!inFlight && (
              <>
                <NotFoundContactUsBar className="scha__srp__not-found-disc mb-2" />
                <div className="scha__srp__disclaimers">
                  <Alert severity={"info"} className={"scha__srp__action-bar__imperial-msg"}>
                    {t("page.srp.unitTypeMessage")}
                  </Alert>
                  <DisclaimerCountryOrigin />
                </div>
                <IncludesSurchargeDisclaimer
                  hasSurcharge={hasSurcharge}
                  tariffSurcharge={tariffSurcharge}
                  className={"mt-30"}
                />
              </>
            )}
          </>
        )}

        <button hidden type="submit" />
      </Stack>
    );
  }

  return (
    <div id={"productResults"}>
      <NoSearchResults variant={appliedFilters.length === 0 ? "search" : "filter"} onClearAll={handleClearAll} />
      <button hidden type="submit" />
    </div>
  );
};

export const selectCurrentPage = (state: RootState) => state.srp.searchParams.currentPage;
const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;

export default memo(ProductResults);
